/**
 * Category data shaping helpers.
 *
 * The backend's /products/getCategories endpoint annotates every category
 * and subcategory with a `productCount` field — the number of currently
 * shoppable products that map to that node (using the same filter the
 * storefront uses: not SingleDose, not lifecycle-hidden, not Rx-only,
 * not soft-deleted). See controllers/products.js → getAllCategories.
 *
 * On the client we use those counts to keep the sidebar / nav clean —
 * empty categories and subcategories shouldn't render. We do this in a
 * helper instead of inline so every consumer (sidebar, top bar, mobile
 * nav) applies the rule the same way.
 *
 * Safety rule for missing counts:
 *   `productCount` is `null` (not 0) when the backend couldn't compute
 *   counts — e.g. an ES outage. We treat `null`/`undefined` as "unknown"
 *   and KEEP those nodes visible so a transient backend issue doesn't
 *   wipe the navigation. Only an explicit `0` hides a node.
 */

/**
 * Was the count computed for this node?
 * @param {object} node — a category or subcategory item from the API
 */
export const hasCount = (node) =>
  node && typeof node.productCount === 'number';

/**
 * Should this node be rendered? True if the count is unknown or > 0.
 * @param {object} node — a category or subcategory item from the API
 */
export const hasProducts = (node) => {
  if (!hasCount(node)) return true; // unknown → show
  return node.productCount > 0;
};

/**
 * Drop empty subcategories from a single category, then drop the category
 * itself if neither it nor any of its subcategories have products.
 *
 * Preserves the original object shape (everything except removed children
 * passes through), so callers don't lose fields they care about.
 *
 * @param {object} category
 * @returns {object|null}
 */
export const stripEmptySubcategories = (category) => {
  if (!category) return null;
  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories.filter(hasProducts)
    : [];
  // If we know the category itself has zero products AND every child is
  // empty, the whole category is hidden.
  if (hasCount(category) && category.productCount === 0 && subcategories.length === 0) {
    return null;
  }
  return { ...category, subcategories };
};

/**
 * Filter an array of categories to those with at least one product
 * somewhere in their tree. Empty subcategories are removed from the
 * categories that remain. Pure — does not mutate input.
 *
 * @param {Array} categories
 * @returns {Array}
 */
export const filterNonEmptyCategories = (categories) => {
  if (!Array.isArray(categories)) return [];
  return categories
    .map(stripEmptySubcategories)
    .filter(Boolean);
};

/**
 * Navigate to a category's product listing the same way every other
 * category entry point does (sidebar, top bar, category list, help cards).
 *
 * The route is keyed by the **stable English category name** (`category.en`),
 * NOT a translated/display string — so navigation is language-independent and
 * always resolves to the right products regardless of the UI language. The
 * German name is stashed in sessionStorage so the results page can show a
 * localized title immediately (it also has its own fallback lookup).
 *
 * Centralized here so callers can't accidentally reintroduce the old
 * "search by translated label" bug or forget the germanName side-effect.
 *
 * @param {function} navigate — react-router navigate
 * @param {string} en — stable English category key (category.category.en)
 * @param {string} [de] — German display name (category.category.de)
 */
/**
 * Localized display name for a taxonomy node (category or subcategory).
 *
 * Prefers the localized `labels` map (de/fr/it/en, populated by the backend
 * scripts/apply_category_labels.js), then the legacy German `de`, then the
 * stable `en` key as a last resort. Safe before labels exist — it simply
 * falls back to German, so it's correct to deploy ahead of the data.
 *
 * `node` is the object that carries the names: for a parent that's
 * `category.category`, for a subcategory it's the subcategory object itself.
 *
 * @param {object} node — { en, de, labels?: {de,fr,it,en} }
 * @param {string} language — active UI language ('de'|'fr'|'it'|'en')
 */
export const pickCategoryName = (node, language) =>
  node?.labels?.[language] || node?.labels?.de || node?.de || node?.en || '';

export const goToCategory = (navigate, en, de) => {
  const key = (en || '').trim();
  if (!key) return;
  try {
    sessionStorage.setItem('germanName', de || key);
  } catch (_) {
    // sessionStorage can throw in private mode / SSR — navigation still works.
  }
  navigate(`/categories/${key}/products`);
};
